import {AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild} from '@angular/core';
import * as L from 'leaflet';

export type ServiceMapAccent = 'bandwidth' | 'market-data';

export interface ServiceMapPopup {
  count: number;
  countLabel: string;
  body: string;
}

export function escapeHtml(value: any): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function serviceMapBlock(title: string, rows: string[]): string {
  if (rows.length === 0) {
    return '';
  }
  return `<div class="smp-block"><div class="smp-block-title">${escapeHtml(title)}</div>${rows.join('')}</div>`;
}

export function serviceMapRow(label: string, value: string): string {
  const labelHtml = label ? `<span class="smp-row-label">${escapeHtml(label)}</span>` : '';
  return `<div class="smp-row">${labelHtml}<span class="smp-row-value">${value}</span></div>`;
}

@Component({
  selector: 'app-service-map',
  templateUrl: './service-map.component.html',
  styleUrls: ['./service-map.component.scss']
})
export class ServiceMapComponent implements AfterViewInit, OnChanges, OnDestroy {

  @Input() title = '';
  @Input() pops: any[] = [];
  @Input() entries: { [trigram: string]: any } = {};
  @Input() accent: ServiceMapAccent = 'bandwidth';
  @Input() loading = false;
  @Input() height = 600;
  @Input() popupBuilder: (pop: any, entry: any) => ServiceMapPopup;

  @ViewChild('mapContainer', {static: true}) mapContainer: ElementRef<HTMLDivElement>;

  markerCount = 0;

  private map: L.Map;
  private readonly markerLayer = L.layerGroup();

  private readonly worldBounds = L.latLngBounds([[-85, -180], [85, 180]]);

  private readonly tileLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    maxZoom: 19,
    noWrap: true,
    bounds: this.worldBounds,
    attribution: '&copy; OpenStreetMap contributors'
  });

  private readonly markerIcon = L.icon({
    iconRetinaUrl: 'assets/leaflet/marker-icon-2x.png',
    iconUrl: 'assets/leaflet/marker-icon.png',
    shadowUrl: 'assets/leaflet/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [0, -38],
    tooltipAnchor: [0, -41],
    shadowSize: [41, 41]
  });

  ngAfterViewInit(): void {
    this.map = L.map(this.mapContainer.nativeElement, {
      zoom: 2,
      center: L.latLng(25, 0),
      layers: [this.tileLayer, this.markerLayer],
      maxBounds: this.worldBounds,
      maxBoundsViscosity: 1,
      zoomSnap: 0,
      zoomDelta: 1,
      fadeAnimation: false
    });
    this.map.on('resize', () => this.applyWorldMinZoom());
    setTimeout(() => {
      if (!this.map) {
        return;
      }
      this.map.invalidateSize();
      this.applyWorldMinZoom();
      this.rebuildMarkers();
    }, 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['pops'] || changes['entries']) && this.map) {
      this.rebuildMarkers();
    }
  }

  ngOnDestroy(): void {
    if (this.map) {
      this.map.remove();
      this.map = undefined;
    }
    this.markerLayer.clearLayers();
  }

  private applyWorldMinZoom(): void {
    if (!this.map) {
      return;
    }
    const worldZoom = this.map.getBoundsZoom(this.worldBounds, true);
    this.map.options.minZoom = worldZoom;
    if (this.map.getZoom() < worldZoom) {
      this.map.setZoom(worldZoom, {animate: false});
    }
  }

  private rebuildMarkers(): void {
    this.applyWorldMinZoom();
    this.markerLayer.clearLayers();
    const markers: L.Marker[] = [];

    for (const pop of this.pops || []) {
      const entry = this.entries ? this.entries[pop.displayId] : undefined;
      const latitude = Number(pop.latitude);
      const longitude = Number(pop.longitude);
      if (entry === undefined || entry === null || !isFinite(latitude) || !isFinite(longitude)) {
        continue;
      }

      const popup = this.popupBuilder ? this.popupBuilder(pop, entry) : {count: 0, countLabel: '', body: ''};
      const marker = L.marker([latitude, longitude], {icon: this.markerIcon, riseOnHover: true});

      marker.bindTooltip(escapeHtml(pop.displayId), {
        permanent: true,
        direction: 'top',
        offset: [0, -6],
        opacity: 1,
        className: `service-map-badge service-map-badge--${popup.count > 0 ? this.accent : 'empty'}`
      });
      marker.bindPopup(this.buildPopupHtml(pop, popup), {
        maxWidth: 320,
        minWidth: 230,
        className: 'service-map-popup'
      });
      marker.on('mouseover', () => this.setBadgeRaised(marker, true));
      marker.on('mouseout', () => this.setBadgeRaised(marker, false));

      marker.addTo(this.markerLayer);
      markers.push(marker);
    }

    this.markerCount = markers.length;
    if (markers.length > 0) {
      const bounds = L.latLngBounds(markers.map(marker => marker.getLatLng())).pad(0.15);
      this.map.fitBounds(bounds, {padding: [40, 40], maxZoom: 6});
    } else {
      this.map.fitBounds(this.worldBounds);
    }
  }

  private setBadgeRaised(marker: L.Marker, raised: boolean): void {
    const tooltip = marker.getTooltip();
    const element = tooltip ? tooltip.getElement() : undefined;
    if (element) {
      element.style.zIndex = raised ? '1000' : '';
    }
  }

  private buildPopupHtml(pop: any, popup: ServiceMapPopup): string {
    const summary = popup.count > 0
      ? `<div class="smp-count">${popup.count} ${popup.countLabel}${popup.count === 1 ? '' : 's'}</div>`
      : `<div class="smp-count smp-count--empty">No ${popup.countLabel} available</div>`;

    return `<div class="smp">`
      + `<div class="smp-head">`
      + `<span class="smp-trigram smp-trigram--${this.accent}">${escapeHtml(pop.displayId)}</span>`
      + `<span class="smp-name">${escapeHtml(pop.name)}</span>`
      + `</div>`
      + summary
      + (popup.body ? `<div class="smp-body">${popup.body}</div>` : '')
      + `</div>`;
  }
}
